import React, { useEffect, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import UndoIcon from "@mui/icons-material/Undo";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import { montrealYmdFromDate } from "../../../pages/components/montrealCalendar";
import AdminPuzGrid from "../../pzl_common/board/AdminPuzGrid";
import CustomStateChip from "../../components/CustomStateChip";
import AdminWordWheelBoardService, {
    DRAFT,
    READY_TO_REVIEW,
    REVIEWED,
} from "../board/AdminWordWheelBoardService";
import { WORD_WHEEL_GRID_SIZE } from "../board/wordWheelGridConstants";
import {
    isValidCrosswordLayout,
    layoutFingerprint,
    scatterWordWheelLayout,
    wordsFromFilledCoordinates,
} from "../board/wordWheelScatterLayout";
import { extractSeasonFromRow, getDisplayPuzzleLevel, hasAssignedPuzzleLevel, isJourneyPack } from "../wordWheelLevelUtils";
import WordWheelMissingClueFlag from "./WordWheelMissingClueFlag";

const PUBLISHABLE_STATES = new Set([DRAFT, READY_TO_REVIEW, REVIEWED]);

function parseFilledCoordinates(raw) {
    if (!raw) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function buildGrid(coords) {
    const size = WORD_WHEEL_GRID_SIZE;
    const square = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));
    for (const coord of coords) {
        for (const pos of coord.positions || []) {
            const letter = pos?.value ?? pos?.letter ?? pos?.char;
            if (letter == null || pos.row < 0 || pos.row >= size || pos.col < 0 || pos.col >= size) {
                continue;
            }
            square[pos.row][pos.col] = String(letter).toUpperCase();
        }
    }
    return square;
}

function wordsForScatter(coords, row) {
    const fromCoords = wordsFromFilledCoordinates(coords);
    if (fromCoords.length >= 3) {
        return fromCoords;
    }
    return String(row?.wordsInUse || "")
        .split(/[\n,;]+/)
        .map((word) => word.trim().toLowerCase())
        .filter((word) => word.length >= 3);
}

function toEpochMs(value) {
    if (value == null || value === "" || value === 0) return null;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return null;
    if (n >= 315_532_800 && n <= 2_500_000_000) return n * 1000;
    return n;
}

function formatPublishedDay(publishedAt) {
    const ms = toEpochMs(publishedAt);
    if (ms == null) return "";
    return montrealYmdFromDate(new Date(ms)).replaceAll("-", "/");
}

/** Compact chip label: 2026/08/29 → 08/29 */
function formatPublishedDayShort(publishedAt) {
    const full = formatPublishedDay(publishedAt);
    if (!full) return "";
    const parts = full.split("/");
    if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}`;
    }
    return full;
}

function stopCardClick(event) {
    event.stopPropagation();
}

const DISCONNECTED_CARD_COLOR = "#ffcdd2";

function layoutWords(coords, row) {
    const fromRow = String(row?.wordsInUse || "")
        .split(/[\n,;]+/)
        .map((word) => word.trim())
        .filter(Boolean);
    if (fromRow.length > 0) {
        return fromRow;
    }
    return wordsFromFilledCoordinates(coords);
}

export default function WordWheelTplGridCard({
    row,
    selected,
    onSelect,
    onOpen,
    onLayoutSaved,
    onNotify,
    onFillMissingClues,
    onRemove,
    onPublish,
    fillingClues = false,
    isTodayDaily = false,
    hideSelect = false,
    wordCount,
    valid,
    levelText = false,
    publishing = false,
}) {
    const savedCoords = useMemo(
        () => parseFilledCoordinates(row?.filledCoordinates),
        [row?.filledCoordinates]
    );
    const [draftCoordinates, setDraftCoordinates] = useState(null);
    const [relocating, setRelocating] = useState(false);
    const [savingLayout, setSavingLayout] = useState(false);

    useEffect(() => {
        setDraftCoordinates(null);
    }, [row?.id, row?.filledCoordinates]);

    const coords = draftCoordinates || savedCoords;
    const grid = useMemo(() => buildGrid(coords), [coords]);
    const hasGrid = coords.some((coord) => Array.isArray(coord?.positions) && coord.positions.length > 0);
    const wordsDisconnected = useMemo(() => {
        if (!hasGrid) {
            return false;
        }
        const words = layoutWords(coords, row);
        if (words.length < 2) {
            return false;
        }
        return !isValidCrosswordLayout(coords, words);
    }, [hasGrid, coords, row]);
    const hasUnsavedLayout = Boolean(draftCoordinates);
    const scatterWords = wordsForScatter(coords, row);
    const canScatterLayout = Boolean(row?.id) && scatterWords.length >= 3 && !relocating && !savingLayout;

    const handleScatterLayout = (event) => {
        stopCardClick(event);
        if (!canScatterLayout) {
            return;
        }
        setRelocating(true);
        try {
            const result = scatterWordWheelLayout(scatterWords, {
                avoidFingerprint: layoutFingerprint(coords),
                seed: Date.now(),
            });
            if (!result.ok) {
                onNotify?.("warning", result.reason);
                return;
            }
            setDraftCoordinates(result.coords);
        } catch (error) {
            Logger.error("Failed to scatter word wheel layout:", error);
            onNotify?.("error", error?.message || "Failed to rearrange words on the grid.");
        } finally {
            setRelocating(false);
        }
    };

    const handleSaveLayout = async (event) => {
        stopCardClick(event);
        if (!row?.id || !draftCoordinates) {
            return;
        }
        setSavingLayout(true);
        try {
            await AdminWordWheelBoardService.updatePuzzleCoordinates(row.id, draftCoordinates);
            const saved = draftCoordinates;
            setDraftCoordinates(null);
            onLayoutSaved?.(row.id, saved);
            onNotify?.("success", "Layout saved.");
        } catch (error) {
            Logger.error("Failed to save word wheel layout:", error);
            onNotify?.("error", error?.message || "Failed to save the new layout.");
        } finally {
            setSavingLayout(false);
        }
    };

    const handleRevertLayout = (event) => {
        stopCardClick(event);
        setDraftCoordinates(null);
    };

    const iconButtonSx = {
        p: 0.5,
    };
    const journeyLocked = isJourneyPack(extractSeasonFromRow(row));
    const puzzleLevel = hasAssignedPuzzleLevel(row) ? getDisplayPuzzleLevel(row) : null;
    const publishedFull = formatPublishedDay(row?.publishedAt);
    const publishedLabel = formatPublishedDayShort(row?.publishedAt);
    const canPublish = Boolean(onPublish) && PUBLISHABLE_STATES.has(row?.state);
    const publishDisabled = !canPublish || journeyLocked || publishing || relocating || savingLayout;
    const hasToolbarMeta = puzzleLevel != null || Boolean(publishedLabel) || isTodayDaily
        || wordCount != null || typeof valid === "boolean";

    return (
        <Paper
            elevation={2}
            title={wordsDisconnected ? "Words are not connected" : undefined}
            sx={{
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                height: "100%",
                bgcolor: wordsDisconnected ? DISCONNECTED_CARD_COLOR : "background.paper",
                outline: hasUnsavedLayout ? "2px solid" : "none",
                outlineColor: "primary.main",
            }}
        >
            <Box
                onClick={() => onOpen?.(row)}
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minHeight: 0,
                    cursor: "pointer",
                    "&:hover": {
                        bgcolor: wordsDisconnected ? "rgba(183, 28, 28, 0.08)" : "action.hover",
                        borderRadius: 1,
                    },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minHeight: 40 }}>
                    {hideSelect ? null : (
                        <Checkbox
                            size="small"
                            checked={Boolean(selected)}
                            onClick={stopCardClick}
                            onChange={(event) => {
                                event.stopPropagation();
                                onSelect?.(row, event.target.checked);
                            }}
                            sx={{ p: 0.5 }}
                            slotProps={{ input: { "aria-label": `Select ${row?.title || "template"}` } }}
                        />
                    )}
                    <Typography
                        variant="subtitle2"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={row?.title || "Untitled"}
                    >
                        {row?.title || "Untitled"}
                    </Typography>
                    {row?.state ? <CustomStateChip label={row.state} size="small" /> : null}
                    <WordWheelMissingClueFlag
                        row={row}
                        filling={fillingClues}
                        onFill={onFillMissingClues}
                    />
                </Box>
                <Box sx={{ width: "100%", aspectRatio: "1 / 1", minHeight: 160, position: "relative" }}>
                    <Box sx={{ position: "absolute", inset: 0 }}>
                        {hasGrid ? (
                            <AdminPuzGrid
                                grid={grid}
                                gridSize={WORD_WHEEL_GRID_SIZE}
                                filledCoordinates={coords}
                                showWordNumbers={false}
                                backgroundColor={wordsDisconnected ? DISCONNECTED_CARD_COLOR : undefined}
                            />
                        ) : (
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    No grid
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 0.5,
                    width: "100%",
                    flexShrink: 0,
                    cursor: "default",
                }}
                onMouseDown={stopCardClick}
                onClick={stopCardClick}
            >
                {hasToolbarMeta ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            flexWrap: "nowrap",
                            overflow: "hidden",
                        }}
                    >
                        {isTodayDaily ? (
                            <Chip
                                size="small"
                                label="Today"
                                color="success"
                                sx={{ minHeight: 28, fontWeight: 700 }}
                            />
                        ) : null}
                        {puzzleLevel != null ? (
                            <Chip
                                size="small"
                                label={levelText ? `Lv ${puzzleLevel}` : puzzleLevel}
                                color="primary"
                                variant="outlined"
                                sx={{ minHeight: 28, fontWeight: 700 }}
                            />
                        ) : null}
                        {wordCount != null ? (
                            <Chip
                                size="small"
                                label={`${wordCount} wds`}
                                variant="outlined"
                                sx={{ minHeight: 28, fontWeight: 700 }}
                            />
                        ) : null}
                        {wordCount != null && wordCount > 10 ? (
                            <Chip
                                size="small"
                                label="Over 10"
                                color="error"
                                sx={{ minHeight: 28, fontWeight: 700 }}
                            />
                        ) : null}
                        {typeof valid === "boolean" ? (
                            <Chip
                                size="small"
                                label={valid ? "Valid" : "Not valid"}
                                color={valid ? "success" : "error"}
                                variant={valid ? "outlined" : "filled"}
                                sx={{ minHeight: 28, fontWeight: 700 }}
                            />
                        ) : null}
                        {publishedLabel ? (
                            <Chip
                                size="small"
                                label={publishedLabel}
                                variant="outlined"
                                title={publishedFull}
                                sx={{
                                    minHeight: 28,
                                    flexShrink: 0,
                                    "& .MuiChip-label": {
                                        px: 0.75,
                                    },
                                }}
                            />
                        ) : null}
                    </Box>
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 0.25 }}>
                {onPublish && PUBLISHABLE_STATES.has(row?.state) ? (
                    <Tooltip
                        title={
                            journeyLocked
                                ? "Season 2026 cannot be published from here"
                                : "Publish this puzzle"
                        }
                        arrow
                    >
                        <span style={{ display: "inline-flex" }}>
                            <IconButton
                                size="small"
                                disabled={publishDisabled}
                                onClick={(event) => {
                                    stopCardClick(event);
                                    if (!publishDisabled) {
                                        onPublish(row);
                                    }
                                }}
                                aria-label="Publish puzzle"
                                sx={{
                                    ...iconButtonSx,
                                    color: journeyLocked ? "text.disabled" : "primary.main",
                                }}
                            >
                                {publishing
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <PublishIcon fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
                <Tooltip title="Scatter words on the grid — keep them connected like a crossword" arrow>
                    <span style={{ display: "inline-flex" }}>
                        <IconButton
                            size="small"
                            disabled={!canScatterLayout}
                            onClick={handleScatterLayout}
                            aria-label="Shuffle word layout"
                            sx={{
                                ...iconButtonSx,
                                color: "primary.main",
                            }}
                        >
                            {relocating
                                ? <CircularProgress size={18} color="inherit" />
                                : <ShuffleIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={hasUnsavedLayout ? "Save this layout" : "Shuffle first to save a new layout"} arrow>
                    <span style={{ display: "inline-flex" }}>
                        <IconButton
                            size="small"
                            disabled={!hasUnsavedLayout || savingLayout}
                            onClick={handleSaveLayout}
                            aria-label="Save word layout"
                            sx={{
                                ...iconButtonSx,
                                color: hasUnsavedLayout ? "primary.main" : "text.disabled",
                            }}
                        >
                            {savingLayout
                                ? <CircularProgress size={18} color="inherit" />
                                : <SaveIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={hasUnsavedLayout ? "Revert to the saved layout" : "No unsaved layout to revert"} arrow>
                    <span style={{ display: "inline-flex" }}>
                        <IconButton
                            size="small"
                            disabled={!hasUnsavedLayout || savingLayout || relocating}
                            onClick={handleRevertLayout}
                            aria-label="Revert word layout"
                            sx={iconButtonSx}
                        >
                            <UndoIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {onRemove ? (
                    <Tooltip
                        title={journeyLocked ? "Season 2026 cannot be removed" : "Remove this template"}
                        arrow
                    >
                        <span style={{ display: "inline-flex" }}>
                            <IconButton
                                size="small"
                                disabled={journeyLocked}
                                onClick={(event) => {
                                    stopCardClick(event);
                                    onRemove(row);
                                }}
                                aria-label="Remove template"
                                sx={{ ...iconButtonSx, color: journeyLocked ? "text.disabled" : "error.main" }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
                </Box>
            </Box>
        </Paper>
    );
}
